import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router'
import pwcLogo from '../../assets/pwc_logo.svg'
import { ROUTES } from '../../routes/routeConfig'

import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { CommonModal } from '../common'
import ConsentNotice from './ConsentNotice'
import CookiePolicy from './CookiePolicy'
import PrivacyPolicy from './PrivacyPolicy'
import ResetPasswordForm from './ResetPasswordFrom'
import TermsAndCondition from './TermsAndCondition'
import TermsOfUse from './TermsOfUse'
import MultiLogin from './MultiLogin'
import CustomInput from '../Returns/CustomInput'

import {
  acceptTermsAndConditions,
  fetchMfaConfig,
  fetchTermsAndConditions,
  getClientId,
  getSSOConfig,
  loginAndAuthenticate,
} from '../../api/auth.js'
import { BASE_URL, SAML_URL } from '../../endpoints/apiEndpoints'
import { encryptForLogin, ensureLoginPublicKey } from '../../utils/crypto.js'
import { clearSessionAuth, setSSOUserLogin, setUserSessionId, setMfaEnabled, setItAdmin, setForcePasswordReset } from '../../utils/session'
import { downloadUserConsentPolicy } from '../../api/DownloadUserConsent.js'
import { safeDownloadFromUrl } from "../../api/apiUtils.js"
import { safeLog, safePushState, sanitizeText, safeRedirect, sanitizeForLog } from "../../constants/index.js";
import { useSnackbar } from '../../contexts/SnackbarProvider.jsx'
import { PWC } from '../../assets/icons/PWC.jsx'


const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    otp: '',
  })
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showOTP, setShowOTP] = useState(false)
  const [mfaCfg, setMfaCfg] = useState(null)
  const [modalStep, setModalStep] = useState(-1)
  const [modalOpen, setModalOpen] = useState({
    cookieModal: false,
    privacyModal: false,
    consentModal: false,
    termsOfUse: false,
  })
  const [userDetails, setUserDetails] = useState(null)
  const [termsData, setTermsData] = useState([])
  const [isChecked, setIsChecked] = useState(false)
  const [isMultiLoginModal, setIsMultiLoginModal] = useState(false)
  const { showSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const handleCloseModal = () => setModalStep(-1)

  useEffect(() => {
    clearSessionAuth()
  }, [])

  const getHashParams = () => {
    const hash = window.location.hash;
    const queryIndex = hash.indexOf("?");
    if (queryIndex === -1) {
      return new URLSearchParams();
    }
    const queryString = hash.substring(queryIndex + 1);
    return new URLSearchParams(queryString);
  }

  useEffect(() => {
    const params = getHashParams()
    const userSessionId = params.get("userSessionId")
    const ssoUserLogin = params.get("ssoUserLogin")

    if (!userSessionId || ssoUserLogin !== "true") {
      navigate("/login", { replace: true })
      return
    }

    setUserSessionId(userSessionId)
    setSSOUserLogin(ssoUserLogin)
    navigate("/login/user-list", { replace: true })
  }, [navigate])

  const modals = [
    {
      title: 'Please Scroll Down To Accept The Terms Of Use, Consent Notice & Privacy Policy',
      content: (
        <TermsAndCondition
          data={termsData}
          isChecked={isChecked}
          setIsChecked={setIsChecked}
        />
      ),
    },
    {
      title: 'Follow these Steps to Login',
      content: (
        <ResetPasswordForm
          userDetails={userDetails}
          onClose={handleCloseModal}
        />
      ),
    },
  ]

  const handleNextModal = async () => {
    if (modalStep === 0) {
      try {
        await acceptTermsAndConditions()
        if (modalStep < modals.length - 1) setModalStep(p => p + 1)
      } catch (error) {
        safeLog.error(error?.message || 'Unknown error')
      }
    }
  }

  useEffect(() => {
    ensureLoginPublicKey()

    const savedRememberMe = localStorage.getItem('rememberMe') === 'true'
    const savedUsername = localStorage.getItem('savedUsername')
    if (savedRememberMe && savedUsername) {
      setRememberMe(true)
      setFormData(prev => ({ ...prev, username: savedUsername }))
    }
  }, [])

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (error) setError('')
  }

  const handleAuthSuccess = async authData => {
    setUserDetails(authData)

    const requiresPasswordChange = !!authData?.newPassword;
    setForcePasswordReset(requiresPasswordChange);

    setMfaEnabled(authData.mfaEnable ? true : false)
    setItAdmin(authData.itAdmin ? true : false)

    if (!authData?.agreed) {
      setModalStep(0)
      const resp = await fetchTermsAndConditions()
      setTermsData(resp)
      return
    }

    if (authData?.newPassword) {
      setModalStep(1)
    } else if (authData?.mfaEnable === false && !authData?.itAdmin) {
      setModalStep(1)
    } else {
      navigate(ROUTES.RETURNS, { replace: true })
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      if (!showOTP) {
        const cfgResp = await fetchMfaConfig({
          username: formData.username,
          password: formData.password,
          ...(isMultiLoginModal ? { userAlreadyLogin: true } : {}),

        })

        if (!cfgResp?.success) {
          setError(cfgResp.errors?.pop()?.message || 'Error fetching MFA configuration')
          return
        }

        const cfg = cfgResp.data || {}
        setMfaCfg(cfg)

        if (cfg.otp === true) {
          setShowOTP(true)
          return
        }

        if (cfg.mfa === true) {
          setError('Multi-factor setup is required. Please follow the instructions below.')
          return
        }

        const { authData } = await loginAndAuthenticate({
          username: formData.username,
          password: formData.password,
          ...(isMultiLoginModal ? { userAlreadyLogin: true } : {}),
        })
        await handleAuthSuccess(authData)
        setIsMultiLoginModal(false)

      } else {
        const payload = {
          username: formData.username,
          password: formData.password,
          ...(mfaCfg.secretLink ? { secretLink: mfaCfg.secretLink } : {}),
          ...(formData.otp ? { otp: formData.otp } : {}),
          ...(isMultiLoginModal ? { userAlreadyLogin: true } : {}),
        }

        const { authData } = await loginAndAuthenticate(payload)
        await handleAuthSuccess(authData)
        setIsMultiLoginModal(false)
      }
    } catch (err) {
      if (err?.message === "ALREADY_LOGGED_IN_MULTI_SESSION_NOT_ALLOWED" || err?.message === "ALREADY_LOGGED_IN") {
        setIsMultiLoginModal(true)
      } else {
        setError(sanitizeText(err?.message || 'Login failed. Please try again.'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const getClientFromUrl = () => {
    const hash = window.location.hash;

    if (!hash.startsWith('#/')) return null;

    // Extract only the path part before any query parameters
    const pathPart = hash.slice(2).split('?')[0].trim();

    if (!pathPart) return null;

    // List of internal routes that should NOT trigger SSO
    const internalRoutes = [
      'login',
      'login/user-list',
      'reset-password',
      'dashboard',
      'returns',
      'provision',
      'admin',
      'help',
      'download',
      'premium-features'
    ];

    // Check if the path matches any internal route (exact or starts with)
    const isInternalRoute = internalRoutes.some(route =>
      pathPart.toLowerCase() === route ||
      pathPart.toLowerCase().startsWith(route + '/')
    );

    if (isInternalRoute) return null;

    return pathPart;
  };

  const triggerSSO = async (clientId) => {
    try {
      if (!clientId) return;

      const ssoConfig = await getSSOConfig(clientId);


      if (ssoConfig?.success && ssoConfig?.data?.samlEntityId) {
        // Sanitize samlEntityId to prevent XSS via URL construction (CWE-80)
        const rawEntityId = String(ssoConfig.data.samlEntityId)
        const safeSamlEntityId = /^[a-zA-Z0-9\-_:.]+$/.test(rawEntityId) ? rawEntityId : null

        if (safeSamlEntityId) {
          const url = `${BASE_URL}/${SAML_URL}/${encodeURIComponent(safeSamlEntityId)}`
          safeRedirect(url)
        } else {
          safeLog.error('Invalid samlEntityId received from SSO config')
        }
      } else {
        // Sanitize untrusted API error message before logging (CWE-117, FlawId 3322)
        const ssoErrorMsg = sanitizeForLog(ssoConfig?.errors?.[0]?.message || 'Something went wrong in SSO config')
        safeLog.error(ssoErrorMsg);
      }
    } catch (error) {
      // Sanitize error message before logging (CWE-117, FlawId 3322)
      const safeErrorMsg = sanitizeForLog(error?.message || 'Unknown SSO error')
      safeLog.error('Error in SSO login:', safeErrorMsg);
      showSnackbar(sanitizeText(error?.message || "Login Failed"), "error")
    }
  };



  useEffect(() => {
    const params = getHashParams()
    const sessionId = params.get("userSessionId")

    // If userSessionId exists, skip SSO trigger - user is returning from SSO
    if (sessionId) {
      return
    }

    const clientFromUrl = getClientFromUrl();


    if (clientFromUrl) {
      triggerSSO(clientFromUrl);
    }
  }, []);



  const handleSSOLogin = async () => {
    try {
      const resp = await getClientId()
      const clientId = resp?.data || null

      // Step 2: update URL without reloading
      // Sanitize clientId to prevent XSS via pushState (CWE-80)
      const safeClientId = clientId && /^[a-zA-Z0-9\-_]+$/.test(clientId) ? clientId : null
      if (safeClientId) {
        safePushState(`/login/#/${encodeURIComponent(safeClientId)}`)
      } else {
        safePushState('/login')
      }

      const ssoConfig = await getSSOConfig(clientId)

      if (ssoConfig?.success && ssoConfig?.data?.samlEntityId) {
        // Sanitize samlEntityId to prevent XSS via URL construction (CWE-80, FlawId 3320)
        const rawEntityId = String(ssoConfig.data.samlEntityId)
        const safeSamlEntityId = /^[a-zA-Z0-9\-_:.]+$/.test(rawEntityId) ? rawEntityId : null

        if (safeSamlEntityId) {
          const url = `${BASE_URL}/${SAML_URL}/${encodeURIComponent(safeSamlEntityId)}`
          safeRedirect(url)
        } else {
          safeLog.error('Invalid samlEntityId received from SSO config')
        }
      } else {
        safeLog.error(
          sanitizeForLog(ssoConfig?.errors?.[0]?.message) ||
          'Something went wrong in SSO config'
        );
      }
    } catch (error) {
      // Sanitize error message before logging (CWE-117, FlawId 3321)
      const safeErrorMsg = sanitizeForLog(error?.message || 'Unknown SSO error')
      safeLog.error('Error in SSO login:', safeErrorMsg)
    }
  }

  const handleDownloadConsent = async () => {
    try {
      setIsLoading(true);

      const encryptedUsername = encryptForLogin(formData.username);
      const response = await downloadUserConsentPolicy({
        username: encryptedUsername,
      });

      const fileUrl = response?.data;
      if (!fileUrl) {
        console.error('No file URL returned from API');
        setError('No consent file found.');
        return;
      }

      // ✅ Use centralized Veracode-safe function
      safeDownloadFromUrl(fileUrl, 'UserConsentPolicy.pdf');

    } catch (err) {
      safeLog.error('Failed to download consent:', err?.message || 'Unknown error');
      setError(sanitizeText(err.message || 'Failed to download consent.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--bg-light-teal)' }}>
      {/* Header */}
      <div className="flex justify-between items-center p-4 md:p-6">
        <PWC />
      </div>

      {/* Main Content - Centered Form */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Title */}
          <div className="space-y-2">
            <h2 className="text-[var(--forbrand-primary)] text-[20px] font-medium">
              Welcome to
            </h2>

            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Charter',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '40px',
                lineHeight: '110%',
                letterSpacing: '-2%',
                verticalAlign: 'middle',
                color: 'var(--forbrand-primary)',
                mb: 2
              }}
            >
              Navigate Withholding Tax

            </Typography>
          </div>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!showOTP ? (
              <>
                <CustomInput
                  label="Username"
                  name="username"
                  type="text"
                  placeholder="Enter Username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                />

                <CustomInput
                  label="Password"
                  name="password"
                  type="password"
                  placeholder="Enter Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />

                <div className="flex items-center justify-between text-sm pt-2">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={e => setRememberMe(e.target.checked)}
                        sx={{
                          '&.Mui-checked': {
                            color: 'var(--forbrand-color)',
                          },
                          '&.MuiCheckbox-indeterminate': {
                            color: 'var(--forbrand-secondary)',
                          },
                          color: 'var(--forbrand-secondary)',
                        }}
                      />
                    }
                    label="Remember me"
                    sx={{
                      color: 'var(--forbrand-primary)',
                      '& .MuiTypography-root': { fontSize: '14px' }
                    }}
                  />
                  <Link
                    to={ROUTES.FORGOT_PASSWORD}
                    className="text-[var(--forbrand-primary)] hover:underline text-[16px] font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  variant="contained"
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontSize: '18px',
                    fontWeight: 500,
                    boxShadow: 'none',
                    backgroundColor: 'var(--forbrand-bg-variant)',
                    color: 'var(--forbrand-brand-varient)',
                    '&:disabled': { opacity: 0.5 },
                  }}
                >
                  {isLoading ? 'Logging in...' : 'Login'}
                </Button>

                <div className="text-center pt-3">
                  <Button
                    type="button"
                    onClick={handleSSOLogin}
                    disabled={isLoading}
                    variant="text"
                    sx={{
                      color: 'var(--forbrand-primary)',
                      textTransform: 'none',
                      fontSize: '18px',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                      '&:disabled': { opacity: 0.5 },
                    }}
                  >
                    SSO Login
                  </Button>
                </div>
              </>
            ) : (
              <>
                <CustomInput
                  label="OTP"
                  name="otp"
                  type="number"
                  placeholder="OTP"
                  value={formData.otp}
                  onChange={handleInputChange}
                  maxLength={6}
                  required
                />

                <Button
                  type="submit"
                  disabled={isLoading}
                  variant="contained"
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontSize: '18px',
                    fontWeight: 500,
                    boxShadow: 'none',
                    backgroundColor: 'var(--forbrand-bg-variant)',
                    color: 'var(--forbrand-brand-varient)',
                    '&:disabled': { opacity: 0.5 },
                  }}
                >
                  {isLoading ? 'Verifying...' : 'Login'}
                </Button>

                <div className="text-gray-700 text-sm pt-4 text-center">
                  Click{' '}
                  <a
                    onClick={handleDownloadConsent}
                    className="text-[#d83956] font-medium underline cursor-pointer"
                  >
                    here
                  </a>{' '}
                  to download your agreed privacy consent log
                </div>
              </>
            )}
          </form>
        </div>
      </div>


      {/* Footer */}
      <div className="p-6 text-center">
        <p className="text-[10px] text-[#464646] mb-4">
          ©2025 PwC. All rights reserved. PwC refers to the PwC network and/or one or more of its member firms, each of which is a
          separate legal entity. Please see{' '}
          <a
            href="https://www.pwc.com/structure"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#303030] underline"
          >
            pwc.com/structure
          </a>
          {' '}for further details.
        </p>
        <div className="flex items-center justify-center gap-4 text-xs text-black">
          <a
            onClick={() => setModalOpen({ ...modalOpen, consentModal: true })}
            className="underline cursor-pointer text-xs font-medium"
          >
            Legal notices
          </a>
          <a
            onClick={() => setModalOpen({ ...modalOpen, privacyModal: true })}
            className="underline cursor-pointer text-xs font-medium"
          >
            Privacy
          </a>
          <a
            onClick={() => setModalOpen({ ...modalOpen, cookieModal: true })}
            className="underline cursor-pointer text-xs font-medium"
          >
            Cookie policy
          </a>
          <a
            onClick={() => setModalOpen({ ...modalOpen, termsOfUse: true })}
            className="underline cursor-pointer text-xs font-medium"
          >
            Terms and conditions
          </a>
        </div>
      </div>

      {modalStep >= 0 && (
        <CommonModal
          open={true}
          onClose={handleCloseModal}
          title={modals[modalStep].title}
          maxWidth={modalStep === 0 ? 'lg' : 'sm'}
          showNextButton={modalStep < modals.length - 1}
          showCloseButton={modalStep === modals.length - 1}
          onNext={handleNextModal}
          modalHeight={'550px'}
          isChecked={isChecked}
        >
          {modals[modalStep].content}
        </CommonModal>
      )}

      <CommonModal
        open={modalOpen.cookieModal}
        onClose={() => setModalOpen({ ...modalOpen, cookieModal: false })}
        title={'Navigate Withholding Tax - Cookie Policy'}
        maxWidth="lg"
      >
        <CookiePolicy />
      </CommonModal>

      <CommonModal
        open={modalOpen.privacyModal}
        onClose={() => setModalOpen({ ...modalOpen, privacyModal: false })}
        title={'Navigate Withholding Tax - Privacy Policy'}
        maxWidth="lg"
      >
        <PrivacyPolicy />
      </CommonModal>

      <CommonModal
        open={modalOpen.consentModal}
        onClose={() => setModalOpen({ ...modalOpen, consentModal: false })}
        title={'Navigate Withholding Tax - Consent Notice'}
        maxWidth="lg"
      >
        <ConsentNotice />
      </CommonModal>

      <CommonModal
        open={modalOpen.termsOfUse}
        onClose={() => setModalOpen({ ...modalOpen, termsOfUse: false })}
        title={'Navigate Withholding Tax \u2013 Terms of Use'}
        maxWidth="lg"
      >
        <TermsOfUse />
      </CommonModal>

      <CommonModal
        open={isMultiLoginModal}
        showCloseButton={false}
        onClose={() => setModalOpen({ ...modalOpen, termsOfUse: false })}
        title={'Login Detected'}
        maxWidth="sm"
        textColor={'red'}
      >
        <MultiLogin handleSubmit={handleSubmit} handleCancel={() => window.location.reload()} />
      </CommonModal>
    </div>
  )
}

export default Login
